import clsx from 'clsx';
import moment from 'moment/moment';
import { type SVGProps, useEffect, useState } from 'react';
import {
  ESCALATION_REASONS,
  type ChatsRequest,
  type ChatsRequestChange,
} from 'src/pages/ConversationsPage/chatsRequest';

const CloseIcon = (props: SVGProps<SVGSVGElement>) => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="10"
      height="9"
      viewBox="0 0 10 9"
      fill="none"
      {...props}
    >
      <path d="M1 0.5L9 8.5M9 0.5L1 8.5" stroke="#E50505" strokeLinecap="round" />
    </svg>
  );
};

// A filter chip; readonly chips are the "All ..." defaults shown when nothing narrows that filter.
interface Chip {
  label: string;
  key?: 'chat_status' | 'types' | 'escalation_reason_category';
  values?: string[];
  readonly?: boolean;
}

const initialChips: Chip[] = [
  {
    label: 'All Statuses',
    key: 'chat_status',
    values: ['open', 'escalated', 'closed', 'auto_closed'],
    readonly: true,
  },
  {
    label: 'All History',
    readonly: true,
  },
  {
    label: 'All Conversations',
    key: 'types',
    values: ['chat', 'playground'],
    readonly: true,
  },
];

const getStatusChips = (request: ChatsRequest): Chip[] => {
  const inProgress = request.chat_status.includes('open');
  const closed = request.chat_status.includes('closed');
  const escalated = request.chat_status.includes('escalated');
  const chips: Chip[] = [];

  if (inProgress && closed && escalated) {
    chips.push(initialChips[0]);
  } else {
    if (inProgress) {
      chips.push({ label: 'In Progress', key: 'chat_status', values: ['open'] });
    }

    if (closed) {
      chips.push({
        label: 'Resolved',
        key: 'chat_status',
        values: ['closed', 'auto_closed'],
      });
    }

    if (escalated) {
      chips.push({
        label: 'Escalated',
        key: 'chat_status',
        values: ['escalated'],
      });
    }
  }

  // Reasons only narrow escalated chats, so they show only while those are listed.
  if (escalated) {
    chips.push(...getEscalationReasonChips(request));
  }

  return chips;
};

const getEscalationReasonChips = (request: ChatsRequest): Chip[] => {
  const chips: Chip[] = [];

  if (!request.escalation_reason_category.length) {
    chips.push({ label: 'All Escalated Reasons', readonly: true });
  }

  ESCALATION_REASONS.filter(({ value }) =>
    request.escalation_reason_category.includes(value),
  ).forEach(({ value, label }) => {
    chips.push({
      label: `${label} Escalation Reason`,
      key: 'escalation_reason_category',
      values: [value],
    });
  });

  return chips;
};

const getTypeChips = (request: ChatsRequest): Chip[] => {
  const chat = request.types.includes('chat');
  const playground = request.types.includes('playground');

  if (chat && playground) {
    return [initialChips[2]];
  }

  const chips: Chip[] = [];

  if (chat) {
    chips.push({
      label: 'Customer Conversations',
      key: 'types',
      values: ['chat'],
    });
  }

  if (playground) {
    chips.push({
      label: 'Playground Conversations',
      key: 'types',
      values: ['playground'],
    });
  }

  return chips;
};

const formatDateRange = (start: string, end: string) =>
  `${moment(start).format('ll')} - ${moment(end).format('ll')}`;

const getDateChips = (request: ChatsRequest): Chip[] => {
  if (!request.updated_at_start && !request.created_at_start) {
    return [initialChips[1]];
  }

  const chips: Chip[] = [];

  if (request.updated_at_start) {
    chips.push({
      label: `Last updated: ${formatDateRange(request.updated_at_start, request.updated_at_end)}`,
    });
  }

  if (request.created_at_start) {
    chips.push({
      label: `Created: ${formatDateRange(request.created_at_start, request.created_at_end)}`,
    });
  }

  return chips;
};

const buildChips = (request: ChatsRequest): Chip[] => [
  ...getStatusChips(request),
  ...getTypeChips(request),
  ...getDateChips(request),
];

export const ConversationChips = ({
  request,
  onChange,
}: {
  request: ChatsRequest | null;
  onChange: (changes: ChatsRequestChange) => void;
}) => {
  const [chips, setChips] = useState(initialChips);

  const removeChip = (chip: Chip) => {
    const isLastUpdated = chip.label.includes('Last updated:');
    const isCreatedAt = chip.label.includes('Created: ');
    const newRequest: ChatsRequestChange = {};

    if (isLastUpdated || isCreatedAt) {
      newRequest.created_at_start = '';
      newRequest.created_at_end = '';
      newRequest.updated_at_start = '';
      newRequest.updated_at_end = '';
    }

    if (chip.key === 'chat_status') {
      newRequest.chat_status = request.chat_status.filter((status) => {
        return !chip.values.includes(status);
      });

      if (!newRequest.chat_status.length) {
        newRequest.chat_status = initialChips[0].values;
      }

      // Escalation reasons only narrow escalated chats, so they go when Escalated does.
      if (chip.values.includes('escalated')) {
        newRequest.escalation_reason_category = [];
      }
    }

    // Removing the last narrowed reason leaves none, which means every reason again.
    if (chip.key === 'escalation_reason_category') {
      newRequest.escalation_reason_category = request.escalation_reason_category.filter(
        (reason) => !chip.values.includes(reason),
      );
    }

    if (chip.key === 'types') {
      newRequest.types = request.types.filter((type) => {
        return !chip.values.includes(type);
      });

      if (!newRequest.types.length) {
        newRequest.types = initialChips[2].values;
      }
    }

    onChange(newRequest);
  };

  useEffect(() => {
    if (request) {
      updateChips(request);
    }
  }, [request]);

  const updateChips = (request: ChatsRequest) => {
    setChips(buildChips(request));
  };

  return (
    <div className={'flex flex-row flex-wrap items-center gap-1.5 mt-2.5'}>
      {chips.map((chip) => {
        return (
          <div
            key={chip.label}
            className={clsx(
              'flex flex-row items-center gap-1.5 text-xs border-[#E3E6E8] rounded-3xl py-2 px-2.5 border capitalize',
              chip.readonly
                ? 'text-ebony_opacity_30 border-dashed'
                : 'text-ebony border-solid hover:border-ebony_opacity_30',
            )}
          >
            <span className={'font-medium'}>{chip.label}</span>
            {!chip.readonly && (
              <button
                type="button"
                aria-label={`Remove ${chip.label}`}
                className={'cursor-pointer'}
                onClick={() => removeChip(chip)}
              >
                <CloseIcon />
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
};
